<div class="tester-page">
  <div class="tester-header">
    <h1>XML template tester</h1>
    <p>
      Type values, pick a country and an event type, and render its submission XML without raising an
      export request. Rendered in your browser against the stored templates — nothing is saved or submitted.
    </p>
  </div>

  <app-error-banner [message]="errorMessage()" />

  @if (isLoading()) {
    <p class="hint">Loading...</p>
  } @else if (availableCountries.length === 0) {
    <app-empty-state message="No country has an XML template configured yet." />
  } @else {
    <div class="toolbar">
      <div class="country-toggle">
        @for (code of availableCountries; track code) {
          <button
            class="toggle-btn"
            [class.toggle-btn--active]="selectedCountry() === code"
            (click)="selectCountry(code)"
          >
            {{ countryLabel(code) }}
          </button>
        }
      </div>

      <div class="mode-toggle">
        <button class="mode-tab" [class.mode-tab--active]="mode() === 'document'" (click)="setMode('document')">
          Full document
        </button>
        <button class="mode-tab" [class.mode-tab--active]="mode() === 'fragment'" (click)="setMode('fragment')">
          Single fragment
        </button>
      </div>
    </div>

    @if (mode() === 'document' && !documentTemplate) {
      <app-empty-state
        [message]="'No document envelope is configured for ' + selectedCountry() + ' — add one on the Country schema page, or switch to Single fragment.'"
      />
    }

    @if (mode() === 'document' && documentFields.length > 0) {
      <section class="card">
        <div class="card__header">
          <div>
            <h2>Document fields</h2>
            <p class="card-hint">
              What the envelope binds about the request. {{ '{{countryCode}}' }} and {{ '{{eventCount}}' }}
              are filled in for you.
            </p>
          </div>
        </div>

        <div class="field-grid">
          @for (field of documentFields; track field) {
            <label class="field">
              <span class="field__label"><code>{{ field }}</code></span>
              <input [(ngModel)]="docValues[field]" [placeholder]="field" />
            </label>
          }
        </div>
      </section>
    }

    <section class="card">
      <div class="card__header">
        <div>
          <h2>{{ mode() === 'fragment' ? 'Event' : 'Events' }}</h2>
          <p class="card-hint">
            Each event renders through its own type's template, falling back to the wildcard. Fields come
            from whatever that template binds.
          </p>
        </div>
        @if (mode() === 'document') {
          <button class="secondary-btn" (click)="addEvent()">
            <span class="material-icons">add</span>
            Add event
          </button>
        }
      </div>

      @if (events.length === 0) {
        <app-empty-state message="No events yet — add one to render anything." />
      } @else {
        @for (event of events; track event.key) {
          <div class="event-row">
            <div class="event-row__header">
              <label class="field field--inline">
                <span class="field__label">Event type</span>
                <select [(ngModel)]="event.eventType" (ngModelChange)="onEventTypeChange()">
                  @for (type of eventTypeOptions; track type) {
                    <option [value]="type">{{ eventTypeLabel(type) }}</option>
                  }
                </select>
              </label>
              @if (mode() === 'document' && events.length > 1) {
                <button class="btn-icon btn-icon--danger" title="Remove" (click)="removeEvent(event.key)">
                  <span class="material-icons">delete_outline</span>
                </button>
              }
            </div>

            @if (fieldsFor(event); as fields) {
              @if (fields.scalars.length === 0 && fields.lists.length === 0) {
                <p class="hint">This event type has no template, so there is nothing to fill in.</p>
              }

              <div class="field-grid">
                @for (term of fields.scalars; track term) {
                  <label class="field" [class.field--withheld]="isWithheld(event, term)">
                    <span class="field__label">
                      <code>{{ term }}</code>
                      @if (isRequired(event, term)) {
                        <span class="tag tag--required">required</span>
                      }
                      @if (isWithheld(event, term)) {
                        <span class="tag tag--withheld">withheld</span>
                      }
                    </span>
                    <input
                      [(ngModel)]="event.scalars[term]"
                      [disabled]="isWithheld(event, term)"
                      [placeholder]="isWithheld(event, term) ? 'Not publishable to this country' : term"
                    />
                  </label>
                }
              </div>

              @for (term of fields.lists; track term) {
                <label class="field field--list" [class.field--withheld]="isWithheld(event, term)">
                  <span class="field__label">
                    <code>{{ term }}</code>
                    <span class="field__hint">one entry per line</span>
                    @if (isWithheld(event, term)) {
                      <span class="tag tag--withheld">withheld</span>
                    }
                  </span>
                  <textarea
                    rows="3"
                    spellcheck="false"
                    [(ngModel)]="event.lists[term]"
                    [disabled]="isWithheld(event, term)"
                    [placeholder]="'urn:epc:id:sgtin:...\nurn:epc:id:sgtin:...'"
                  ></textarea>
                </label>
              }
            }
          </div>
        }
      }
    </section>

    <div class="generate-row">
      <button class="primary-btn" (click)="generate()" [disabled]="events.length === 0">
        <span class="material-icons">play_arrow</span>
        Generate XML
      </button>
    </div>

    @if (result(); as res) {
      <section class="card">
        @if (!res.xml) {
          <p class="preview-note preview-note--warn">
            <span class="material-icons">error_outline</span>
            <span>{{ res.problem }} <code>{{ res.problemCode }}</code></span>
          </p>
        } @else {
          @if (res.unresolved.length > 0) {
            <p class="preview-excluded">
              Resolved to nothing, so rendered empty: {{ res.unresolved.join(', ') }}
            </p>
          }

          <div class="output-header">
            <span class="meta">{{ selectedCountry() }} · {{ events.length }} event(s)</span>
            <div class="output-actions">
              <button class="btn-icon" title="Copy" (click)="copyXml()">
                <span class="material-icons">content_copy</span>
              </button>
              <button class="secondary-btn" (click)="downloadXml()">
                <span class="material-icons">download</span>
                Download .xml
              </button>
            </div>
          </div>

          <pre class="xml-output">{{ res.xml }}</pre>
        }
      </section>
    }
  }
</div>
